import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { getRemainingChatMessages } from "@/services/api/assistantChatService"
import React, { createContext, useCallback, useContext, useState } from "react"

interface RateLimitContextType {
  remainingMessages: number | null
  usedMessages: number
  isUnlimited: boolean
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const RateLimitContext = createContext<RateLimitContextType | undefined>(
  undefined,
)

export const RateLimitProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  // React Compiler opt-in (vite.config.mts runs it in annotation mode): the
  // context value is memoized, so consumers re-render only on real changes.
  "use memo"
  const chatRateLimitEnabled = useFeatureFlag("CHAT_RATE_LIMIT")

  const [state, setState] = useState({
    remainingMessages: null as number | null,
    usedMessages: 0,
    isUnlimited: !chatRateLimitEnabled,
    isLoading: chatRateLimitEnabled,
    error: null as string | null,
  })

  const refresh = useCallback(async () => {
    if (!chatRateLimitEnabled) {
      setState({
        remainingMessages: null,
        usedMessages: 0,
        isUnlimited: true,
        isLoading: false,
        error: null,
      })
      return
    }

    try {
      setState((prev) => ({ 
        ...prev, 
        isLoading: prev.remainingMessages === null, 
        error: null 
      }))
      const data = await getRemainingChatMessages()
      setState({
        remainingMessages: data.remaining_messages,
        usedMessages: data.used_messages,
        isUnlimited: data.is_unlimited,
        isLoading: false,
        error: null,
      })
    } catch {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Unable to refresh limit status",
      }))
    }
  }, [chatRateLimitEnabled, getRemainingChatMessages])

  // Initial load
  React.useEffect(() => {
    if (!chatRateLimitEnabled) {
      return
    }

    refresh()
  }, [refresh, chatRateLimitEnabled])

  return (
    <RateLimitContext.Provider value={{ ...state, refresh }}>
      {children}
    </RateLimitContext.Provider>
  )
}

export const useRateLimitContext = () => {
  const context = useContext(RateLimitContext)
  if (!context) {
    throw new Error("useRateLimitContext must be used within RateLimitProvider")
  }
  return context
}
